import { cn } from '#/utils/cn';

import { Icon } from './icons';
import { resolveLinkTarget } from './linkTarget';

interface NewsCardLink {
  label: string;
  href: string;
  target?: string;
  rel?: string;
}

export interface NewsCardProps {
  title: string;
  content: string;
  links?: NewsCardLink[];
  className?: string;
  /** Optional slot rendered below the body copy (e.g. a ticker price summary). */
  children?: React.ReactNode;
  /** GTM `data-track` id, forwarded onto every rendered link anchor. */
  dataTrack?: string;
  /** GTM `data-track-context`, forwarded onto every rendered link anchor. */
  dataTrackContext?: string;
}

export function NewsCard({
  title,
  content,
  links,
  className,
  children,
  dataTrack,
  dataTrackContext,
}: NewsCardProps) {
  const cardClasses = cn(
    'news-card border-blue-20 bg-gray-10 @container h-full rounded border shadow-(--shadow-card)',
    className,
  );

  return (
    <article className={cardClasses}>
      <div className="px-(--spacing-news-card-padding-inline-base) py-(--spacing-news-card-padding-block-base) @min-[34rem]:px-(--spacing-news-card-padding-inline-wide) @min-[34rem]:py-(--spacing-news-card-padding-block-wide) @min-[64rem]:p-(--spacing-news-card-padding-roomy)">
        <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-24 @min-[34rem]:grid-cols-[minmax(0,1fr)_minmax(var(--spacing-news-card-links-min-width),max-content)] @min-[34rem]:gap-x-32 @min-[34rem]:gap-y-24">
          <div className="@min-[34rem]:max-w-news-card-body-max-width min-w-0 space-y-16">
            <h2 className="text-fg-header text-fluid-subheader leading-tight font-semibold">
              {title}
            </h2>

            <p className="text-fluid-base text-fg leading-relaxed">{content}</p>

            {children}
          </div>

          {links?.length ? (
            <div className="@min-[34rem]:mt-news-card-links-offset flex flex-col items-start gap-20 justify-self-start">
              {links.map(({ label, href, target, rel }) => {
                const linkProps = resolveLinkTarget({ href, target, rel });

                return (
                  <a
                    key={`${label}-${href}`}
                    href={href}
                    target={linkProps.target}
                    rel={linkProps.rel}
                    aria-label={label}
                    data-track={dataTrack}
                    data-track-context={dataTrackContext}
                    className="group/news-link text-fluid-base-sm tracking-link-caps inline-flex items-center gap-12 leading-none font-semibold uppercase no-underline"
                  >
                    <span>{label}</span>
                    <Icon
                      id="arrow-right"
                      size={18}
                      aria-hidden="true"
                      className="transition-transform group-hover/news-link:translate-x-4"
                    />
                  </a>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}
